import { REFACTOR_URL } from '../../../helpers/env'
import { API_URL } from '../constants'
import {
  ICreatePatient,
  INote,
  IStatus,
  IAddPatientCenter,
  PatientType,
  IAddImplant,
  IPatientInfos,
  IUpdateImplant,
  IAffectTransmitter,
  IUpdateProsthesisId,
} from '../reducers'
import { IDoctor } from '../reducers'
import { GridSortDirection } from '@mui/x-data-grid-pro'

export const countryService = (token: string) => {
  const ENDPOINT: RequestInfo = `${API_URL}/country`

  const parameters: RequestInit = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Host: `${API_URL}`,
      Authorization: `Bearer ${token}`,
    },
    mode: 'cors',
  }

  let status = 0

  return fetch(ENDPOINT, parameters)
    .then((response) => {
      status = response.status
      return response.json()
    })
    .then((data) => {
      return { data, status }
    })
    .catch((e) => {
      console.error(e)
    })
}

export const companyService = (token: string, page: number, size: number) => {
  const ENDPOINT: RequestInfo = `${API_URL}/company?page=${page}&size=${size}`

  const parameters: RequestInit = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Host: `${API_URL}`,
      Authorization: `Bearer ${token}`,
    },
    mode: 'cors',
  }

  let status = 0

  return fetch(ENDPOINT, parameters)
    .then((response) => {
      status = response.status
      return response.json()
    })
    .then((data) => {
      return { data, status }
    })
    .catch((e) => {
      console.error(e)
    })
}

export const prosthesisGammeRangeService = (
  token: string,
  page: number,
  size: number,
) => {
  const ENDPOINT: RequestInfo = `${API_URL}/prosthesis/gamme-range?page=${page}&size=${size}`

  const parameters: RequestInit = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Host: `${API_URL}`,
      Authorization: `Bearer ${token}`,
    },
    mode: 'cors',
  }

  let status = 0

  return fetch(ENDPOINT, parameters)
    .then((response) => {
      status = response.status
      return response.json()
    })
    .then((data) => {
      return { data, status }
    })
    .catch((e) => {
      console.error(e)
    })
}

export const patientService = (
  token: string,
  page: number,
  size: number,
  patientsType: PatientType,
  searchText: string,
  gammeIds: string[],
  makerIds: number[],
  sort: { field: string; sort: GridSortDirection } | null,
) => {
  const ENDPOINT: RequestInfo = `${REFACTOR_URL}/patients?page=${page}&limit=${size}&type=${patientsType}&search=${searchText}${
    gammeIds.length > 0
      ? gammeIds.map((gammeId) => `&prosthesisTypes[]=${gammeId}`).join('')
      : ''
  }${
    makerIds.length
      ? makerIds.map((makerId) => `&makerIds[]=${makerId}`).join('')
      : ''
  }${sort ? `&sort=${sort.field}&order=${sort.sort}` : ''}`

  const parameters: RequestInit = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Host: `${API_URL}`,
      Authorization: `Bearer ${token}`,
    },
    mode: 'cors',
  }

  let status = 0

  return fetch(ENDPOINT, parameters)
    .then((response) => {
      status = response.status
      return response.json()
    })
    .then((data) => {
      return { data, status }
    })
    .catch((e) => {
      console.error(e)
    })
}

export const getPatientsSummary = (token: string) => {
  const ENDPOINT: RequestInfo = `${REFACTOR_URL}/patients/summary`

  const parameters: RequestInit = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Host: `${API_URL}`,
      Authorization: `Bearer ${token}`,
    },
    mode: 'cors',
  }

  let status = 0

  return fetch(ENDPOINT, parameters)
    .then((response) => {
      status = response.status
      return response.json()
    })
    .then((data) => {
      return { data, status }
    })
    .catch((e) => {
      console.error(e)
    })
}

export const addPatientService = (token: string, patient: ICreatePatient) => {
  const ENDPOINT: RequestInfo = `${REFACTOR_URL}/patients`

  return fetchPost(token, ENDPOINT, { ...patient })
}

export const updatePatientAllowBilling = (
  token: string,
  patientId: number,
  allow: boolean,
) => {
  const ENDPOINT: RequestInfo = `${REFACTOR_URL}/patients/${patientId}/allowBilling`

  return fetchPatch(token, ENDPOINT, { allowBilling: allow })
}

export const getPatientByIdService = (token: string, id: number) => {
  const ENDPOINT: RequestInfo = `${REFACTOR_URL}/patients/${id}`
  const parameters: RequestInit = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Host: `${API_URL}`,
      Authorization: `Bearer ${token}`,
    },
    mode: 'cors',
  }

  let status = 0

  return fetch(ENDPOINT, parameters)
    .then((response) => {
      status = response.status
      return response.json()
    })
    .then((data) => {
      return { data, status }
    })
    .catch((e) => {
      console.error(e)
    })
}

export const updatePatientInfo = (token: string, patient: IPatientInfos) => {
  // Bascule de l'API Java (PATCH /patient, id dans le corps) vers api-nest
  // (PATCH /patients/:id). Le Java faisait un save d'entité complète, qui
  // écrasait historyComment, fev et treatmentComment — absents de ce corps —
  // à chaque modification de fiche.
  const ENDPOINT: RequestInfo = `${REFACTOR_URL}/patients/${patient.id}`

  const parameters: RequestInit = {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Host: `${API_URL}`,
      Authorization: `Bearer ${token}`,
    },
    mode: 'cors',
    body: JSON.stringify(patient),
  }

  let status = 0

  return fetch(ENDPOINT, parameters)
    .then((response) => {
      status = response.status
      return response.json()
    })
    .then((data) => {
      return { data, status }
    })
    .catch((e) => {
      console.error(e)
    })
}

export const addPatientNoteService = (token: string, note: INote) => {
  const ENDPOINT: RequestInfo = `${API_URL}/patient/${note.patientID}/note`

  return fetchPost(token, ENDPOINT, { ...note })
}

export const changePatientStatusService = (token: string, status: IStatus) => {
  const ENDPOINT: RequestInfo = `${API_URL}/patient/${status.patientID}/status`

  return fetchPost(token, ENDPOINT, { ...status })
}

export const addPatientCenter = (
  token: string,
  id: number,
  center: IAddPatientCenter,
) => {
  const ENDPOINT: RequestInfo = `${API_URL}/patient/${id}/center`

  return fetchPost(token, ENDPOINT, { ...center })
}

export const addImplant = (token: string, infos: IAddImplant) => {
  const ENDPOINT: RequestInfo = `${REFACTOR_URL}/crm-implantation`

  const parameters: RequestInit = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Host: `${REFACTOR_URL}`,
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      ...infos,
    }),
    mode: 'cors',
  }

  let status = 0

  return fetch(ENDPOINT, parameters)
    .then((response) => {
      status = response.status
      return response.json()
    })
    .then((data) => {
      return { data, status }
    })
    .catch((e) => {
      console.error(e)
    })
}

export const updatePatientDoctor = (
  token: string,
  patientId: number,
  doctor: IDoctor,
) => {
  const ENDPOINT: RequestInfo = `${API_URL}/patient/${patientId}/patientdoctor`

  const parameters: RequestInit = {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(doctor),
    mode: 'cors',
  }

  let status = 0

  return fetch(ENDPOINT, parameters)
    .then((response) => {
      status = response.status
      return response.json()
    })
    .then((data) => {
      return { data, status }
    })
    .catch((e) => {
      console.error(e)
    })
}

export const updatePatientHistory = (
  token: string,
  patientId: number,
  history: number[],
) => {
  const ENDPOINT: RequestInfo = `${API_URL}/patient/${patientId}/history`

  const body: any[] = []
  history.forEach((h) => body.push({ historyTypeId: h, patientId }))

  return fetchPost(token, ENDPOINT, body)
}

export const updatePatientTreatment = (
  token: string,
  patientId: number,
  treatment: number[],
) => {
  const ENDPOINT: RequestInfo = `${API_URL}/patient/${patientId}/treatment`

  const body: any[] = []
  treatment.forEach((t) => body.push({ treatmentTypeId: t, patientId }))

  return fetchPost(token, ENDPOINT, body)
}

export const updateImplant = (
  token: string,
  infos: IUpdateImplant | IUpdateProsthesisId,
) => {
  const ENDPOINT: RequestInfo = `${REFACTOR_URL}/crm-implantation/${infos.id}`
  console.log(`request service with ${JSON.stringify(infos)}`)

  const parameters: RequestInit = {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(infos),
    mode: 'cors',
  }

  let status = 0

  return fetch(ENDPOINT, parameters)
    .then((response) => {
      status = response.status
      return response.json()
    })
    .then((data) => {
      return { data, status }
    })
    .catch((e) => {
      console.error(e)
    })
}

export const deleteImplant = (
  token: string,
  patientId: number,
  implantId: number,
) => {
  const ENDPOINT: RequestInfo = `${REFACTOR_URL}/crm-implantation/${implantId}`

  const parameters: RequestInit = {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    mode: 'cors',
  }

  let status = 0

  return fetch(ENDPOINT, parameters)
    .then((response) => {
      status = response.status
      return response.json()
    })
    .then((data) => {
      return { data, status }
    })
    .catch((e) => {
      console.error(e)
    })
}

export const updatePatientReasons = (
  token: string,
  implantationId: number,
  reasons: number[],
) => {
  const ENDPOINT: RequestInfo = `${API_URL}/implantation/${implantationId}/reason`

  const body: any[] = []
  reasons.forEach((reasonId) => body.push({ reasonId, implantationId }))

  const parameters: RequestInit = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
    mode: 'cors',
  }

  let status = 0

  return fetch(ENDPOINT, parameters)
    .then((response) => {
      status = response.status
      return response.json()
    })
    .then((data) => {
      return { data, status }
    })
    .catch((e) => {
      console.error(e)
    })
}

export const getTransmitterReferences = (token: string) => {
  const ENDPOINT: RequestInfo = `${API_URL}/transmitter-reference?page=0&size=1000`

  const parameters: RequestInit = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    mode: 'cors',
  }

  let status = 0

  return fetch(ENDPOINT, parameters)
    .then((response) => {
      status = response.status
      return response.json()
    })
    .then((data) => {
      return { data, status }
    })
    .catch((e) => {
      console.error(e)
    })
}

export const createTransmitter = (
  token: string,
  referenceId: number,
  serialNumber: string,
  method: 'POST' | 'PATCH',
  transmitterId?: number,
) => {
  const ENDPOINT: RequestInfo =
    method === 'POST'
      ? `${REFACTOR_URL}/transmitter`
      : `${REFACTOR_URL}/transmitter/${transmitterId}`

  const body: { referenceId: number; serialNumber: string; id?: number } = {
    referenceId,
    serialNumber,
  }
  if (method === 'PATCH') body.id = transmitterId

  const parameters: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    mode: 'cors',
    body: JSON.stringify(body),
  }

  let status = 0

  return fetch(ENDPOINT, parameters)
    .then((response) => {
      status = response.status
      return response.json()
    })
    .then((data) => {
      return { data, status }
    })
    .catch((e) => {
      console.error(e)
    })
}

export const affectTransmitter = (
  token: string,
  infos: IAffectTransmitter,
  method: 'POST' | 'PATCH',
) => {
  console.log(`data to update ${JSON.stringify(infos)}`)
  const ENDPOINT: RequestInfo =
    method === 'POST'
      ? `${REFACTOR_URL}/transmitter-affectation`
      : `${REFACTOR_URL}/transmitter-affectation/${infos.id}`

  const parameters: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    mode: 'cors',
    body: JSON.stringify(infos),
  }

  let status = 0

  return fetch(ENDPOINT, parameters)
    .then((response) => {
      status = response.status
      return response.json()
    })
    .then((data) => {
      return { data, status }
    })
    .catch((e) => {
      console.error(e)
    })
}

export const postAgreement = (
  token: string,
  transmitterId: number,
  file: string,
) => {
  const ENDPOINT: RequestInfo = `${API_URL}/transmitter/${transmitterId}/affectation/agreement`
  const formData = new FormData()
  formData.append('file', file)
  formData.append('displayName', 'essaideFichier')

  const parameters: RequestInit = {
    method: 'POST',
    body: formData,
    headers: {
      Authorization: `Bearer ${token}`,
    },
    mode: 'cors',
  }

  let status = 0

  return fetch(ENDPOINT, parameters)
    .then((response) => {
      status = response.status
      return response.json()
    })
    .then((data) => {
      return { data, status }
    })
    .catch((e) => {
      console.error(e)
    })
}

export const deleteTransmitter = (
  token: string,
  transmitterId: number,
  affectationId: number,
) => {
  const ENDPOINT: RequestInfo = `${REFACTOR_URL}/transmitter-affectation/${affectationId}`

  const parameters: RequestInit = {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    mode: 'cors',
  }

  let status = 0

  return fetch(ENDPOINT, parameters)
    .then((response) => {
      status = response.status
      return response.json()
    })
    .then((data) => {
      return { data, status }
    })
    .catch((e) => {
      console.error(e)
    })
}

export const getStoredFile = (token: string, storeFileId: number) => {
  const ENDPOINT: RequestInfo = `${REFACTOR_URL}/stored_file/${storeFileId}`

  return fetchGetBlob(token, ENDPOINT)
}

export const generatePerPatient = (token: string, patientId: number) => {
  console.log(`service generatePerPatient with patientId ${patientId}`)
  const ENDPOINT: RequestInfo = `${REFACTOR_URL}/prescriptions/patient`

  return fetchPost(token, ENDPOINT, { patientId })
}

export const crmEnrollment = (token: string, implantId: number) => {
  console.log(`service Enrollment with implantId ${implantId}`)
  const ENDPOINT: RequestInfo = `${REFACTOR_URL}/crm-implantation-enrollment`

  return fetchPost(token, ENDPOINT, { implantId })
}
export const crmSend = (token: string, implantId: number) => {
  console.log(`service Sendwith implantId ${implantId}`)
  const ENDPOINT: RequestInfo = `${REFACTOR_URL}/crm-implantation-enrollment/send`

  return fetchPost(token, ENDPOINT, { implantId })
}

const fetchPost = (token: string, endPoint: string, object: object) => {
  const parameters: RequestInit = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Host: `${API_URL}`,
      Authorization: `Bearer ${token}`,
    },
    mode: 'cors',
    body: JSON.stringify(object),
  }

  let status = 0

  return fetch(endPoint, parameters)
    .then((response) => {
      status = response.status
      return response.json()
    })
    .then((data) => {
      return { data, status }
    })
    .catch((e) => {
      console.error(e)
    })
}
const fetchPatch = (token: string, endPoint: string, object: object) => {
  const parameters: RequestInit = {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Host: `${API_URL}`,
      Authorization: `Bearer ${token}`,
    },
    mode: 'cors',
    body: JSON.stringify(object),
  }

  let status = 0

  return fetch(endPoint, parameters)
    .then((response) => {
      status = response.status
      return response.json()
    })
    .then((data) => {
      return { data, status }
    })
    .catch((e) => {
      console.error(e)
    })
}

const fetchGetBlob = (token: string, endPoint: string) => {
  const parameters: RequestInit = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Host: `${API_URL}`,
      Authorization: `Bearer ${token}`,
    },
    mode: 'cors',
  }

  let status = 0

  return fetch(endPoint, parameters)
    .then((response) => {
      status = response.status
      return response.blob()
    })
    .then((data) => {
      return { data, status }
    })
    .catch((e) => {
      console.log(e)
    })
}
